import { getClient } from '../utils/client';
import { error, success } from '../utils/logger';
import Table from 'cli-table3';
import chalk from 'chalk';
import { formatJSON, formatNushell } from '../utils/formatter';
import { getConfigValue } from '../utils/config';

export interface ListTablesOptions {
  format?: 'table' | 'json' | 'nushell';
}

export interface CreateTableOptions {
  data?: string;
  firstRowHeader?: boolean;
}

export interface UpdateTableOptions {
  name: string;
}

/**
 * List tables from a Baserow database.
 * @param database The database ID (optional).
 * @param options List options.
 * @returns The list of tables.
 */
export async function listTables(database: number | undefined, options: ListTablesOptions = {}): Promise<any[]> {
  const client = await getClient();
  if (!database) {
    const configDatabaseId = await getConfigValue('database');
    if (!configDatabaseId) {
      throw new Error('Database ID is required. Provide it as an argument or set it in your config.');
    }
    database = parseInt(configDatabaseId as string);
  }
  const tables = await client.databaseTables.list(database);
  return tables;
}

/**
 * Get a specific table by ID.
 * @param tableId The table ID.
 * @param options List options.
 * @returns The table object.
 */
export async function getTable(tableId: number, options: ListTablesOptions = {}): Promise<any> {
  const client = await getClient();
  const table = await client.databaseTables.get(tableId);
  return table;
}

/**
 * Create a new table in a Baserow database.
 * @param database The database ID (optional).
 * @param name The name of the new table.
 * @param options Create options.
 * @returns The created table object.
 */
export async function createTable(database: number | undefined, name: string, options: CreateTableOptions = {}): Promise<any> {
  const client = await getClient();
  if (!database) {
    const configDatabaseId = await getConfigValue('database');
    if (!configDatabaseId) {
      throw new Error('Database ID is required. Provide it as an argument or set it in your config.');
    }
    database = parseInt(configDatabaseId as string);
  }
  const payload: any = { name };
  if (options.data) {
    try {
      payload.data = JSON.parse(options.data);
    } catch (err: unknown) {
      if (err instanceof Error) {
        throw new Error(`Invalid JSON data: ${err.message}`);
      } else {
        throw new Error('Invalid JSON data: An unknown error occurred');
      }
    }
  }
  if (options.firstRowHeader !== undefined) {
    payload.first_row_header = options.firstRowHeader;
  }
  const newTable = await client.databaseTables.create(database, payload);
  return newTable;
}

/**
 * Update an existing table in Baserow.
 * @param tableId The table ID.
 * @param options Update options.
 * @returns The updated table object.
 */
export async function updateTable(tableId: number, options: UpdateTableOptions): Promise<any> {
  const client = await getClient();
  const updatedTable = await client.databaseTables.update(tableId, { name: options.name });
  return updatedTable;
}

/**
 * Delete a table from Baserow.
 * @param tableId The table ID.
 * @returns An object with status and message.
 */
export async function deleteTable(tableId: number): Promise<{ status: 'success'; message: string }> {
  const client = await getClient();
  await client.databaseTables.delete(tableId);
  return { status: 'success', message: `Table ${tableId} successfully deleted` };
} 